import {Injectable, inject} from '@angular/core';
import {Observable, of, Subscription, forkJoin} from "rxjs";
import {filter, map} from "rxjs";
import {TYPE} from "../types/types";
import {ApiService} from "./api.service";
import {HttpEventType, HttpResponse} from "@angular/common/http";
import {FileStorageResource} from '../types/settings-types/fileStorageResource';
import {FileStorageType} from '../types/settings-types/fileStorageType';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  private apiService: ApiService = inject(ApiService);

  public allowedImageTypes: string[] = ['gif', 'jpg', 'jpeg', 'png'];
  public allowedVideoTypes: string[] = ['mov', 'mp4'];
  public allowedAudioTypes: string[] = ['mp3'];
  public allowedMediaTypes: string[] = [
    ...this.allowedImageTypes,
    ...this.allowedVideoTypes, 
    ...this.allowedAudioTypes
  ];
  public documentsGalleryDialog: boolean = false;
  public documentsGalleryHideReactions: boolean = false;
  public documentsGalleryForwardDialog: boolean = false;
  public documentsGalleryFiles: FileStorageResource[] = [];
  public documentsGalleryPosition: number = 0;
  public documentsGalleryMessageId: number = 0;
  public documentsGalleryFileToMessageMap: Map<number, number> = new Map<number, number>();
  public documentsGalleryShowChatOptions: boolean = false;
  private fileSubscription: Subscription[] = [];

  public uploadFiles(documents: FileList, directory?: string, isPublic?: boolean): Observable<FileStorageResource[]> {
    if (documents.length === 0) {
      return of([]);
    }

    const uploadObservables = Array.from(documents).map((document) =>
      this.apiService.storageUpload(document, directory, isPublic).pipe(
        filter((event): event is HttpResponse<FileStorageResource> => event.type === HttpEventType.Response),
        map((response) => response.body!)
      )
    );

    return forkJoin(uploadObservables);
  }

  public downloadDocument(document: FileStorageResource): void {
    if (!document.path) {
      return;
    }
    const fileName = document.filename || 'download';
    this.apiService.downloadByPath(document.path).subscribe({
      next: (blob: Blob) => this.triggerBlobDownload(blob, fileName),
      error: (error: unknown) => console.error('Download failed:', error)
    });
  }

  public downloadData(data: BlobPart, fileName: string, mimeType = 'application/octet-stream'): void {
    this.triggerBlobDownload(new Blob([data], {type: mimeType}), fileName);
  }

  public downloadFromUrl(url: string, fileName: string): void {
    if (!url) {
      return;
    }
    if (url.startsWith('blob:')) {
      this.triggerUrlDownload(url, fileName);
      return;
    }
    this.apiService.downloadByUrl(url).subscribe({
      next: (blob: Blob) => this.triggerBlobDownload(blob, fileName),
      error: (error: unknown) => console.error('Download failed:', error)
    });
  }

  public mergeDocuments(...documentsArrays: FileStorageResource[][]): FileStorageResource[] {
    return documentsArrays.flat();
  }

  public getDocumentByType(documents: FileStorageResource[], type: FileStorageType): FileStorageResource[] {
    return documents.filter((document) => document.type === type);
  }

  public getDocumentClass(document: FileStorageResource): 'file-default' | 'file-excel' | 'file-word' | 'file-pdf' | 'file-zip' {
    const extension = this.getDocumentExtension(document);
    if (extension === 'pdf') {
      return 'file-pdf';
    } else if (extension === 'xls' || extension === 'xlsx') {
      return 'file-excel';
    } else if (extension === 'doc' || extension === 'docx') {
      return 'file-word';
    } else if (extension === 'zip') {
      return 'file-zip';
    } else {
      return 'file-default';
    }
  }

  public getLocalDocumentClass(document: TYPE.LocalDocument): 'file-default' | 'file-excel' | 'file-word' | 'file-pdf' | 'file-zip' {
    const extension = document.filename.split('.').pop()!.toLowerCase();
    if (extension === 'pdf') {
      return 'file-pdf';
    } else if (extension === 'xls' || extension === 'xlsx') {
      return 'file-excel';
    } else if (extension === 'doc' || extension === 'docx') {
      return 'file-word';
    } else if (extension === 'zip') {
      return 'file-zip';
    } else {
      return 'file-default';
    }

  }

  public getDocumentExtension(document: FileStorageResource): string {
    // if (document.localFile) {
    //   return document.localFile.name.split('.').pop()!;
    // }
    return document.filename!.split('.').pop()!;
  }

  public getLocalDocumentExtension(document: TYPE.LocalDocument): string {
    return document.filename.split('.').pop()!.toLowerCase();
  }

  public showGallery(
    media: FileStorageResource[],
    document: FileStorageResource,
    hideReactions: boolean = true,
    messageId?: number,
    fileToMessageMap?: Map<number, number>,
    showChatOptions: boolean = false
  ): void {
    this.documentsGalleryDialog = true;
    this.documentsGalleryFiles = media;
    this.documentsGalleryPosition = media.indexOf(document) !== -1 ? media.indexOf(document) : media.findIndex((file) => file.id === document.id);
    this.documentsGalleryHideReactions = hideReactions;
    this.documentsGalleryMessageId = messageId ?? 0;
    this.documentsGalleryFileToMessageMap = fileToMessageMap ?? new Map();
    this.documentsGalleryShowChatOptions = showChatOptions;
  }

  public cancelUploads(): void {
    this.fileSubscription.forEach((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
    this.fileSubscription = [];
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    try {
      this.triggerUrlDownload(blobUrl, fileName);
    } finally {
      window.URL.revokeObjectURL(blobUrl);
    }
  }

  private triggerUrlDownload(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      document.body.removeChild(link);
    }
  }
}
